import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { setOrderAction } from '../../app/actions/SetOrderAction';
import { contentCountsUpToDate } from '../../app/actions/spaces/ContentCountsActions';
import { enterSpace } from '../../app/actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../app/actions/spaces/FavoritesActions';
import { requestSpaceContents } from '../../app/actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../../app/actions/spaces/SetPreviousUrlReducerAction';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import {
  ConnectedQueryParamUpdateProvider,
  QueryParamUpdateProviderOwnProps
} from '../../app/spaces/RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../../app/spaces/RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { DataRoleSubspaceName, SpaceInfo } from '../../app/state/SpaceInfo';
import { MapDispatchToProps } from '../../app/utils/ConnectUtils';
import { BrowserTitleService } from '../../commonInfra/services/BrowserTitleService';
import { Projects } from '../../projects/resources/Projects';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { Filter } from '../../ts/helpers/Filter';
import { Omit } from '../../ts/helpers/TypescriptHelpers';
import { IDataRoleDetailInfo, IDataRoleInfo } from '../../ts/models/IDataRoleInfo';
import { IFavoritableItemInfo } from '../../ts/models/resourceInfoParts';
import { DataRoles } from '../../ts/resources/Dataroles';
import { IDataRoleActions } from '../../ts/ServerApi/types/IDataRoleActions';
import { ListValueConcepts } from '../apis/ListValueConcepts';
import { ConnectedDataRoleSpace } from './ConnectedDataRoleSpace';
import { DataRoleSpaceOwnProps, DataRoleSpaceProps } from './DataRoleSpace';

export interface DataRoleSpaceProviderOwnProps {
  readonly routeLocationSearch: string;
  readonly siteName: string;
  readonly subspaceName: DataRoleSubspaceName;
  readonly dataRoleId: string;
  readonly urls: SpacesSiteUrls;
  readonly renderDataRoleSpace: (props: DataRoleSpaceOwnProps) => JSX.Element;
  readonly fetchedAllData: () => void;
}

export interface DataRoleSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
}

export type DataRoleSpaceProviderContainerProps = DataRoleSpaceProviderOwnProps & DataRoleSpaceProviderDispatchProps;

export interface DataRoleSpaceProviderContainerState {
  readonly dataRoleActions: IDataRoleActions;
  readonly dataRoleInfo: IDataRoleInfo;
  readonly dataRoleDetailInfo: IDataRoleDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
}

const InitialState: DataRoleSpaceProviderContainerState = {
  dataRoleActions: undefined,
  dataRoleDetailInfo: undefined,
  dataRoleInfo: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false
};

export class DataRoleSpaceProviderContainer extends React.PureComponent<DataRoleSpaceProviderContainerProps, DataRoleSpaceProviderContainerState> {

  private unmounted: boolean = false;

  public constructor(props: DataRoleSpaceProviderContainerProps){
    super(props);
    this.state = InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderDataRoleSpace({
        allowedActions: this.state.dataRoleActions,
        dataRoleDetailInfoWithUsage: {
          usageInfo: this.state.dataRoleInfo.usageInfo,
          usageInfoMaxValues: this.state.dataRoleInfo.usageInfoMaxValues,
          ...this.state.dataRoleDetailInfo,
        },
        subspaceProps: {
          siteUrls: this.props.urls,
          dataRoleDetailInfo: this.state.dataRoleDetailInfo,
        },
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite
      });
    } else if (this.state.hasError) {
      return <RouterError errorType={'dataroles'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: DataRoleSpaceProviderContainerProps) {
    if (this.changedDataRole(prevProps)) {
      this.updateState(InitialState /* want the initial state for the new data role */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace(false);

      this.setState({initialRender: false, renderChildren: true});
      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.dataRoleDetailInfo.name });

    } else if (prevProps.subspaceName !== this.props.subspaceName) {
      this.changeSubspace(true);
    }
  }

  private getData = () => {
    this.getDataRole();
    this.getDataRoleDetail();
    this.getActions();
  }

  private changedDataRole(prevProps: DataRoleSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.dataRoleId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.dataRoleId !== this.props.dataRoleId);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.dataRoleInfo) &&
      !_.isUndefined(this.state.dataRoleDetailInfo) &&
      !_.isUndefined(this.state.dataRoleActions);
  }

  private getDataRoleDetail = async () => {
    try {
      const dataRoleDetail = await DataRoles.getById(this.props.dataRoleId);
      const getValueConcepts = (luid: string) => ListValueConcepts(luid).then(x => x.value_concepts);

      if (FeatureFlags.dataRolesEditValueSynonymsEnabled) {
        getValueConcepts(dataRoleDetail.dataRole.field_concept_key)
            .then((valueConcepts) => {
              dataRoleDetail.dataRole.value_concepts = valueConcepts;
              this.updateState({dataRoleDetailInfo: dataRoleDetail});
            });
      } else {
        this.updateState({dataRoleDetailInfo: dataRoleDetail});
      }
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getDataRole = async () => {
    try {
      const dataRole = await DataRoles.all()
        .withFilters([Filter.fieldEq('id', this.props.dataRoleId)])
        .getFirst();
      this.updateState({dataRoleInfo: dataRole});
      const projectHierarchy = await Projects.getProjectHierarchy(dataRole.project.id);
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      populateContentBreadcrumbs([ ...projectHierarchyWithType, dataRole ], this.props.urls);
      this.props.fetchedAllData();
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getActions = async () => {
    try {
      const actions = await DataRoles.actions(this.props.dataRoleId);
      this.updateState({dataRoleActions: actions});
    } catch (e) {}
  }

  private updateState<K extends keyof DataRoleSpaceProviderContainerState>(newState: Pick<DataRoleSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }

  private changeSubspace = (shouldNotFetchCounts: boolean) => {
    this.props.enterSpace(
      {
        item: this.state.dataRoleDetailInfo,
        spaceType: 'dataRole',
        subspaceName: this.props.subspaceName
      }
    );

    // If we just switched tabs we know content counts are up to date.
    // They will get set to false by the above EnterSpaceAction.
    if(shouldNotFetchCounts) {
      this.props.contentCountsUpToDate();
    }

    this.props.requestSpaceContents();
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.dataRoleDetailInfo);
  }

  private unfavorite = () => {
   this.props.removeContentFavorite(this.state.dataRoleDetailInfo);
  }
}

const mapDispatchToProps: MapDispatchToProps<DataRoleSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterSpace,
  addContentFavorite,
  removeContentFavorite,
  setOrderAction,
  setPreviousUrl,
}, dispatch);

export const ConnectedDataRoleSpaceProviderContainer: React.ComponentClass<DataRoleSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(DataRoleSpaceProviderContainer);

const renderDataRoleSpace = (props: DataRoleSpaceProps): JSX.Element => <ConnectedDataRoleSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type DataRoleSpaceProviderProps = LocationPathProp & Omit<DataRoleSpaceProviderOwnProps, 'renderDataRoleSpace' | 'fetchedAllData'>;

export interface DataRoleSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export class DataRoleSpaceProvider extends React.PureComponent<DataRoleSpaceProviderProps, DataRoleSpaceProviderState> {
  public constructor(props: DataRoleSpaceProviderProps) {
    super(props);
    this.state = {fetchedAllData: false};
  }

  public render() {
    const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
      routeLocationPath: this.props.routeLocationPath,
      routeLocationSearch: this.props.routeLocationSearch,
      shouldUpdateContentTypes: false,
      shouldUpdateFilters: false,
      shouldUpdateSort: false,
      shouldUpdateBreadcrumbs: this.state.fetchedAllData
    };

    const DataRoleSpaceInfo: SpaceInfo = {
      item: null,
      spaceType: 'dataRole',
      subspaceName: this.props.subspaceName,
    };

    return (
      <ConnectedSpaceInfoProvider spaceInfo={DataRoleSpaceInfo}>
        <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
          <ConnectedDataRoleSpaceProviderContainer
            {...this.props}
            renderDataRoleSpace={renderDataRoleSpace}
            fetchedAllData={this.fetchedAllData} />
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
  }

  private fetchedAllData = () => {
    this.setState({fetchedAllData: true});
  }
}
